// The geometry of the video page (PRD-0074, slice 03): where each tracked person is at a moment
// of the recording, which passes are on screen, and where the picture sits inside the player.
// Pure — the player and the pass strip under `components/` only draw what these answer.
import type { VideoObjectTrack, VideoPlateTrack, VideoTrack } from '@/services/api/video'

/** A box in 0–1 of the upright frame: `[x1, y1, x2, y2]`. */
export type TrackBox = readonly [number, number, number, number]

/** What the overlay draws for one pass at one moment. */
export interface TrackAtTime {
  trackId: number
  box: TrackBox
  /** The pass's face, so the label can be a name; null for a pass without one. */
  face: VideoTrack['face']
}

/** A sample lies within this many seconds of the current time to count as "now" at the pass's ends. */
const EDGE_TOLERANCE_S = 0.25

/** The pass's box at *t*: interpolated between the two samples around it, held at its ends. */
export function boxAt(track: VideoTrack, t: number): TrackBox | null {
  const samples = track.samples
  if (samples.length === 0) return null
  const first = samples[0]
  const last = samples[samples.length - 1]
  if (t < first[0] - EDGE_TOLERANCE_S || t > last[0] + EDGE_TOLERANCE_S) return null
  if (t <= first[0]) return [first[1], first[2], first[3], first[4]]
  if (t >= last[0]) return [last[1], last[2], last[3], last[4]]
  // Binary search for the sample pair around t; passes hold hundreds of samples.
  let low = 0
  let high = samples.length - 1
  while (high - low > 1) {
    const mid = (low + high) >> 1
    if (samples[mid][0] <= t) low = mid
    else high = mid
  }
  const a = samples[low]
  const b = samples[high]
  const span = b[0] - a[0]
  const share = span > 0 ? (t - a[0]) / span : 0
  const mix = (i: number) => a[i] + (b[i] - a[i]) * share
  return [mix(1), mix(2), mix(3), mix(4)]
}

/** Every pass on screen at *t*, each with its box — what the overlay draws on one frame. */
export function tracksAt(tracks: readonly VideoTrack[], t: number): TrackAtTime[] {
  const out: TrackAtTime[] = []
  for (const track of tracks) {
    const box = boxAt(track, t)
    if (box) out.push({ trackId: track.track_id, box, face: track.face })
  }
  return out
}

/** Where the picture sits inside a player of `width × height` that keeps the video's aspect. */
export interface ContentRect {
  left: number
  top: number
  width: number
  height: number
}

/** The letterboxed picture rectangle of a `videoWidth × videoHeight` picture in a `width × height` element. */
export function contentRect(
  width: number,
  height: number,
  videoWidth: number,
  videoHeight: number
): ContentRect {
  if (width <= 0 || height <= 0 || videoWidth <= 0 || videoHeight <= 0) {
    return { left: 0, top: 0, width: Math.max(0, width), height: Math.max(0, height) }
  }
  const scale = Math.min(width / videoWidth, height / videoHeight)
  const drawnWidth = videoWidth * scale
  const drawnHeight = videoHeight * scale
  return {
    left: (width - drawnWidth) / 2,
    top: (height - drawnHeight) / 2,
    width: drawnWidth,
    height: drawnHeight,
  }
}

/** A box's CSS inside the picture rectangle, in pixels of the player element. */
export function boxStyle(box: TrackBox, rect: ContentRect) {
  const clamp = (v: number) => Math.min(1, Math.max(0, v))
  const x1 = clamp(Math.min(box[0], box[2]))
  const y1 = clamp(Math.min(box[1], box[3]))
  const x2 = clamp(Math.max(box[0], box[2]))
  const y2 = clamp(Math.max(box[1], box[3]))
  return {
    left: rect.left + x1 * rect.width,
    top: rect.top + y1 * rect.height,
    width: Math.max(0, (x2 - x1) * rect.width),
    height: Math.max(0, (y2 - y1) * rect.height),
  }
}

/** What "Play pass" asks the player to do: run one pass with a second of lead-in and lead-out. */
export interface PassPlayRequest {
  /** The pass emphasised while it plays; 0 for a span that is no person's pass (a plate's). */
  trackId: number
  from: number
  to: number
  /** Changes with every request, so asking for the same pass twice plays it twice. */
  nonce: number
}

/** The seconds a pass is played with on either side, so it is not cut on the first frame. */
export const PASS_PADDING_S = 1

export function passPlayRequest(
  track: VideoTrack,
  nonce: number,
  duration: number
): PassPlayRequest {
  return {
    trackId: track.track_id,
    from: Math.max(0, track.t_start - PASS_PADDING_S),
    to: Math.min(duration > 0 ? duration : Number.POSITIVE_INFINITY, track.t_end + PASS_PADDING_S),
    nonce,
  }
}

/** A span to play with the padding on either side, emphasising no pass (a plate's reading). */
export function spanPlayRequest(
  from: number,
  to: number,
  nonce: number,
  duration: number
): PassPlayRequest {
  return {
    trackId: 0,
    from: Math.max(0, from - PASS_PADDING_S),
    to: Math.min(duration > 0 ? duration : Number.POSITIVE_INFINITY, to + PASS_PADDING_S),
    nonce,
  }
}

/** Plates on the strip and the picture (slice 07): one bar per reading in the tone of its link status. */
export interface PlateBar {
  plateId: string
  trackId: number
  text: string
  status: VideoPlateTrack['link_status']
  /** 0–1 of the recording's duration. */
  start: number
  end: number
}

export function plateBars(plates: readonly VideoPlateTrack[], duration: number): PlateBar[] {
  if (duration <= 0) return []
  return [...plates]
    .sort((a, b) => a.t_start - b.t_start || a.track_id - b.track_id)
    .map(plate => ({
      plateId: plate.plate_id,
      trackId: plate.track_id,
      text: plate.text,
      status: plate.link_status,
      start: Math.min(1, Math.max(0, plate.t_start / duration)),
      end: Math.min(1, Math.max(0, plate.t_end / duration)),
    }))
}

/** Every plate on screen at *t*, each with its box and text — drawn beside the people. */
export interface PlateAtTime {
  plateId: string
  text: string
  status: VideoPlateTrack['link_status']
  box: TrackBox
}

export interface ObjectAtTime {
  trackId: number
  label: string
  box: TrackBox
}

/** The vehicles and other objects in the picture at *t*, each at its interpolated box. */
export function objectsAt(objects: readonly VideoObjectTrack[], t: number): ObjectAtTime[] {
  const out: ObjectAtTime[] = []
  for (const object of objects) {
    const box = boxAt({ samples: object.samples } as VideoTrack, t)
    if (box) out.push({ trackId: object.track_id, label: object.label, box })
  }
  return out
}

export function platesAt(plates: readonly VideoPlateTrack[], t: number): PlateAtTime[] {
  const out: PlateAtTime[] = []
  for (const plate of plates) {
    const box = boxAt({ samples: plate.samples } as VideoTrack, t)
    if (box) out.push({ plateId: plate.plate_id, text: plate.text, status: plate.link_status, box })
  }
  return out
}

/** The track a face came from, by the face id the rail selected; null for a photo's face. */
export function trackOfFace(
  tracks: readonly VideoTrack[],
  faceId: string | null
): VideoTrack | null {
  if (!faceId) return null
  return tracks.find(track => track.face?.face_id === faceId) ?? null
}

/** Passes in time order with their share of the recording, for the strip under the player. */
export interface PassBar {
  trackId: number
  /** 0–1 of the recording's duration. */
  start: number
  end: number
  state: 'confirmed' | 'suggested' | 'unassigned' | 'none'
  label: string | null
}

export function passBars(tracks: readonly VideoTrack[], duration: number): PassBar[] {
  if (duration <= 0) return []
  return [...tracks]
    .sort((a, b) => a.t_start - b.t_start || a.track_id - b.track_id)
    .map(track => ({
      trackId: track.track_id,
      start: Math.min(1, Math.max(0, track.t_start / duration)),
      end: Math.min(1, Math.max(0, track.t_end / duration)),
      state: track.face ? track.face.state : 'none',
      label: track.face?.display_name ?? null,
    }))
}
